import {
  Field,
  Input,
  NativeSelect,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";

/** One answer. Files are absent on purpose -- see the note on the component. */
export type ReviewFieldValue = string | number | boolean | null;

export type ReviewFormField = {
  key: string;
  label: string;
  description?: string | null;
  type: string;
  required: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
};

/**
 * The form a reviewer fills in when signing.
 *
 * Deliberately narrower than the assignee's form: no file fields. A reviewer's
 * assessment is an answer, not an upload, and supporting one here would mean
 * the whole blob-upload lifecycle -- pending files, cleanup of superseded
 * ones -- for a case nothing has asked for. The editor does not offer the type
 * on a review form either, so the two agree.
 */
export function ReviewFormFields({
  fields,
  values,
  onChange,
  disabled = false,
}: {
  fields: ReviewFormField[];
  values: Record<string, ReviewFieldValue>;
  onChange: (key: string, value: ReviewFieldValue) => void;
  disabled?: boolean;
}) {
  return (
    <Stack gap={4}>
      {fields.map((field) => {
        const value = values[field.key] ?? null;

        if (field.type === "boolean") {
          return (
            <Field.Root key={field.key}>
              <Switch.Root
                colorPalette="blue"
                disabled={disabled}
                checked={Boolean(value ?? false)}
                onCheckedChange={(e) => onChange(field.key, e.checked)}
              >
                <Switch.HiddenInput />
                <Switch.Control />
                <Switch.Label fontSize="sm">
                  {field.label}
                  {field.required ? <Text as="span" ml={1} color="red.500">*</Text> : null}
                </Switch.Label>
              </Switch.Root>
              {field.description ? <Field.HelperText>{field.description}</Field.HelperText> : null}
            </Field.Root>
          );
        }

        return (
          <Field.Root key={field.key} required={field.required}>
            <Field.Label fontSize="sm">
              {field.label}
              <Field.RequiredIndicator />
            </Field.Label>
            {field.description ? (
              <Text mb={1} fontSize="xs" color="fg.muted">{field.description}</Text>
            ) : null}

            {field.type === "textarea" ? (
              <Textarea
                minH={20}
                disabled={disabled}
                value={String(value ?? "")}
                onChange={(event) => onChange(field.key, event.target.value)}
              />
            ) : field.type === "select" ? (
              <NativeSelect.Root disabled={disabled}>
                <NativeSelect.Field
                  value={String(value ?? "")}
                  onChange={(event) => onChange(field.key, event.target.value)}
                >
                  <option value="">Select…</option>
                  {(field.options ?? []).map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            ) : field.type === "range" ? (
              <Stack gap={2} w="full">
                <Slider.Root
                  colorPalette="blue"
                  disabled={disabled}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={[typeof value === "number" ? value : Number(field.min ?? 0)]}
                  onValueChange={(e) => onChange(field.key, e.value[0] ?? null)}
                >
                  <Slider.Control>
                    <Slider.Track><Slider.Range /></Slider.Track>
                    <Slider.Thumb index={0}><Slider.HiddenInput /></Slider.Thumb>
                  </Slider.Control>
                </Slider.Root>
                <Text fontSize="sm" color="fg.muted">
                  Value: {typeof value === "number" ? value : "—"}
                </Text>
              </Stack>
            ) : (
              <Input
                disabled={disabled}
                type={field.type === "date"
                  ? "date"
                  : field.type === "time"
                    ? "time"
                    : field.type === "url"
                      ? "url"
                      : field.type === "email"
                        ? "email"
                        : field.type === "number"
                          ? "number"
                          : "text"}
                value={String(value ?? "")}
                onChange={(event) =>
                  onChange(
                    field.key,
                    field.type === "number"
                      // An empty number box is "unanswered", not zero.
                      ? (event.target.value === "" ? null : Number(event.target.value))
                      : event.target.value,
                  )}
              />
            )}
          </Field.Root>
        );
      })}
    </Stack>
  );
}

/**
 * Which required answers are still missing.
 *
 * Returned as labels rather than a boolean so the composer can say which one,
 * and checked here rather than only in the browser's own validation because
 * the fields are rendered outside a form element.
 */
export function missingRequiredReviewFields(
  fields: ReviewFormField[],
  values: Record<string, ReviewFieldValue>,
): string[] {
  return fields
    .filter((field) => {
      if (!field.required) {
        return false;
      }
      const value = values[field.key];
      // False is a real answer to a required switch; empty text is not.
      if (typeof value === "boolean" || typeof value === "number") {
        return false;
      }
      return value === undefined || value === null || String(value).trim() === "";
    })
    .map((field) => field.label);
}
